import React from 'react';
import '../../styles/components/AsperaConnectInstaller.scss';
import { pushTransfers, setConnectStatus, setupTransferMonitor, runningWithAsperaMobile, setupConnectOnlyTransferMonitor, switchToDesktopSDK, optIntoDesktopMode } from '../../helpers/transfer';
import { clientEncrypt, getEnvironmentVar, isFirefox, isMobile, isSafari } from '../../helpers/helpers';
import { getStorageItem, observableTimeout, openNewTab, removeStorageItem, setStorageItem } from '../../helpers/starterHelpers';
import { storageKeys } from '../../constants/constants';
import { AsperaError } from '../../classes/AsperaError';
import { notification } from '../../services/NotificationService';
import { language } from '../../services/LanguageService';
import { tracking } from '../../services/TrackingService';
import { ImageConnectFlow } from '../../svg/ImageConnectFlow';
import { Button, Link, ListItem, Modal, ProgressStep, Theme, getIcon, getIconComponent, UnorderedList } from '../Carbon';
import { Connect } from '@ibm-aspera/connect-sdk-js';
import { init, launch, testConnection, getInstallerInfo, registerStatusCallback } from '@ibm-aspera/sdk';
import { SubManager } from '../../classes/SubManager';
import { from, fromEvent, mergeMap, Subject, throttleTime } from 'rxjs';
import { AsperaOverflowButton } from '../AsperaOverflowButton';
import { desktopAppExample, onePixelClearImage } from '../../constants/data';
import { changeTransferClient } from '../../helpers/transfer';
import { promptForReload } from '../../helpers/components';
import { AsperaTooltip } from '../AsperaTooltip';
import { modal } from '../../services/ModalService';
import { ModalBodyConfirm } from '../ModalBodyConfirm';
/**
 * React Component
 *
 * The AsperaConnectInstaller provides the custom connect and http gateway handling flow for Aspera Starter apps.
 * AsperaAppWrapper renders this component and should have already called initTransferSystems (which is also needed by AsperaHeader).
 *
 * @example
 * ```javascript
 * <AsperaConnectInstaller appName={appName} />
 * ```
 */
export class AsperaConnectInstaller extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            view: 'NONE',
            hasFocused: false,
            expandDesktop: false,
            focusDesktopInstall: false,
            desktopDragPosition: 0,
            isDesktopSupportOpened: false,
        };
        this.subManager = new SubManager();
        // Link for Desktop download; this is returned asynchronously
        this.linkForDesktopDownload = [];
        // Track what was last requested state for welcome to install screen.
        this.lastInstallState = 'PLUGIN';
        // Track if desktop INIT has successfully been called
        this.desktopHasBeenInit = false;
        /** Desktop installer width */
        this.desktopInstallerWidth = 400;
        // Track the last desktop status for event
        this.lastDesktopStatus = '';
        // Track the position of the desktop installer
        this.dragPositionSubject = new Subject();
        this.connectStatusEventListener = (eventType, eventStatus) => {
            const { transferSystemData, currentApp } = this.props;
            if (eventType === Connect.EVENT.STATUS) {
                setConnectStatus(eventStatus);
                // If Connect is running reset skip and load properly
                if (getStorageItem(storageKeys.skipTransferSystem)) {
                    if (eventStatus === Connect.STATUS.RUNNING) {
                        removeStorageItem(storageKeys.skipTransferSystem);
                        setupConnectOnlyTransferMonitor();
                    }
                    return;
                }
                if ((currentApp === null || currentApp === void 0 ? void 0 : currentApp.id) === 'help') {
                    return;
                }
                switch (eventStatus) {
                    case Connect.STATUS.EXTENSION_INSTALL:
                        this.lastInstallState = 'PLUGIN';
                        if (getStorageItem(storageKeys.skipTransferSystemWelcome) || transferSystemData.skipWelcome) {
                            this.setState({ view: 'PLUGIN' });
                        }
                        else {
                            this.setState({ view: 'WELCOME' });
                        }
                        break;
                    case Connect.STATUS.FAILED:
                        this.lastInstallState = 'INSTALL';
                        if (getStorageItem(storageKeys.skipTransferSystemWelcome) || transferSystemData.skipWelcome) {
                            this.setState({ view: 'INSTALL' });
                        }
                        else {
                            this.setState({ view: 'WELCOME' });
                        }
                        break;
                    case Connect.STATUS.OUTDATED:
                        this.setState({ view: 'UPDATE' });
                        break;
                    case Connect.STATUS.RUNNING:
                        this.setState({ view: 'NONE' });
                        break;
                }
            }
        };
        this.handleJson = (data) => {
            const links = data.links;
            const oneClick = links.filter(link => link.rel === 'enclosure-one-click')[0];
            const backup = links.filter(link => link.rel === 'enclosure')[0];
            this.linkForDownload = (oneClick === null || oneClick === void 0 ? void 0 : oneClick.hrefAbsolute) || (backup === null || backup === void 0 ? void 0 : backup.hrefAbsolute);
        };
        this.handleSkip = (event) => {
            if (event && typeof event.stopPropagation === 'function') {
                event.stopPropagation();
            }
            setStorageItem(storageKeys.skipTransferSystem, 'TRUE');
            tracking.track('Started Process', {
                process: 'Connect Skip Installation',
                successFlag: true,
                processType: 'connect',
            });
            this.setState({ view: 'NONE' });
            pushTransfers([]);
            setupTransferMonitor();
        };
        this.goToInstall = () => {
            setStorageItem(storageKeys.skipTransferSystemWelcome, 'TRUE');
            tracking.updateUserPreferences({ skipConnectWelcomeScreen: true });
            this.setState({ view: this.lastInstallState });
            tracking.track('Started Process', {
                process: 'Close Welcome Screen',
                successFlag: true,
                processType: 'connect',
            });
        };
        this.installExtension = () => {
            var _a, _b;
            openNewTab((_b = (_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.asperaInstaller) === null || _b === void 0 ? void 0 : _b.getExtensionStoreLink(), true);
            tracking.track('Started Process', {
                process: 'Connect Install Extension',
                successFlag: true,
                processType: 'connect',
            });
        };
        this.installConnect = () => {
            if (this.linkForDownload) {
                openNewTab(this.linkForDownload, true);
                this.timer = setTimeout(() => {
                    this.setState({ view: 'ENABLE' });
                }, 5000);
                tracking.track('Started Process', {
                    process: 'Connect Download App',
                    successFlag: true,
                    processType: 'connect',
                });
            }
            else {
                notification.create('error', language.get('transferSystem.connectNotReadyToDownload'), language.get('transferSystem.connectNotReadyToDownloadText'));
                tracking.track('Started Process', {
                    process: 'Connect Download App',
                    successFlag: false,
                    processType: 'connect',
                });
            }
        };
        this.refreshPage = () => {
            tracking.track('Started Process', {
                process: 'Connect Reload Page',
                successFlag: true,
                processType: 'connect',
            });
            window.location.reload();
        };
        this.useConnectInstead = () => {
            setStorageItem(storageKeys.transferSystemChoice, 'CONNECT');
            tracking.updateUserPreferences({ optIntoDesktop: false }, true);
        };
        this.toggleDesktopMultiUserMode = () => {
            const isEnabled = getStorageItem(storageKeys.enableDesktopMultiUser);
            if (isEnabled) {
                removeStorageItem(storageKeys.enableDesktopMultiUser);
            }
            else {
                setStorageItem(storageKeys.enableDesktopMultiUser, 'TRUE');
            }
            tracking.track('Started Process', {
                process: `${isEnabled ? 'Disabled' : 'Enabled'} Multi User Mode`,
                successFlag: true,
                processType: 'connect',
            });
            promptForReload(this.subManager);
        };
        this.getPlatformString = (item) => {
            const platformTypeArch = `${item.platform}-${item.type}-${item.arch}`;
            switch (platformTypeArch) {
                case 'macos-dmg-universal':
                    return language.get('transferSystem.macUniversal');
                case 'windows-msi-x64':
                    return language.get('transferSystem.windowsMSI');
                case 'linux-deb-x64':
                    return language.get('transferSystem.linuxDeb');
                case 'linux-appimage-x64':
                    return language.get('transferSystem.linuxAppImage');
                default:
                    return language.get('transferSystem.unknownPlatform');
            }
        };
        this.handleDesktopDrag = (event) => {
            this.dragPositionSubject.next(event.clientX);
        };
        this.handleDragStart = (event) => {
            var _a;
            let emptyImage = document.querySelector('#aspera-empty-image');
            if (!emptyImage) {
                emptyImage = document.createElement('img');
                emptyImage.id = 'aspera-empty-image';
                emptyImage.src = onePixelClearImage;
                (_a = document.querySelector('body')) === null || _a === void 0 ? void 0 : _a.appendChild(emptyImage);
            }
            event.dataTransfer.setDragImage(emptyImage, 0, 0);
        };
        this.handleDragEnd = (event) => {
            let position = event.clientX - 16;
            const windowMax = window.innerWidth;
            if (position < 30) {
                position = 24;
            }
            else if ((windowMax - position) < (this.desktopInstallerWidth + 30)) {
                this.setState({ desktopDragPosition: 24 });
                return;
            }
            this.setState({ desktopDragPosition: windowMax - (position + this.desktopInstallerWidth) });
        };
        this.initDesktopSdk = () => {
            if (this.desktopHasBeenInit) {
                this.setState({ view: 'NONE', expandDesktop: false });
                return;
            }
            this.subManager.setSubscription(from(init({ appId: this.appId, supportMultipleUsers: !!getStorageItem(storageKeys.enableDesktopMultiUser) })).pipe(mergeMap(() => switchToDesktopSDK())).subscribe({
                next: () => {
                    this.desktopHasBeenInit = true;
                    this.setState({ view: 'NONE', expandDesktop: false });
                },
                error: error => {
                    new AsperaError('AsperaConnectInstaller: Desktop SDK was unable to load', error);
                    notification.create('error', language.get('transferSystem.unableToStartDesktop'));
                },
            }));
        };
        this.installDesktop = (item) => {
            openNewTab(item.url, true);
            tracking.track('Started Process', {
                process: 'Desktop Download App',
                successFlag: true,
                processType: 'connect',
            });
        };
        this.launchDesktop = (event) => {
            if (event && typeof event.stopPropagation === 'function') {
                event.stopPropagation();
            }
            launch();
            tracking.track('Started Process', {
                process: 'Desktop Launch App',
                successFlag: true,
                processType: 'connect',
            });
        };
        this.prepareDesktopInstaller = (alreadyRunning) => {
            const { transferSystemData } = this.props;
            const installerGetError = (error) => {
                new AsperaError('AsperaConnectInstaller: Desktop SDK was unable to get installation data', error);
                notification.create('error', language.get('transferSystem.unableToStartDesktop'));
            };
            if (transferSystemData.autoLaunchDesktop && !alreadyRunning) {
                launch();
            }
            this.subManager.setSubscription(from(getInstallerInfo()).subscribe({
                next: data => {
                    var _a;
                    if ((_a = data === null || data === void 0 ? void 0 : data.entries) === null || _a === void 0 ? void 0 : _a.length) {
                        this.linkForDesktopDownload = data.entries;
                        if (!alreadyRunning) {
                            this.setState({ view: 'DESKTOP' });
                        }
                    }
                    else {
                        installerGetError();
                    }
                },
                error: error => {
                    installerGetError(error);
                },
            }));
        };
        this.desktopStatusChange = (status) => {
            switch (status) {
                case 'CLOSED':
                    if (this.lastDesktopStatus !== 'CLOSED') {
                        this.setState({ view: 'DESKTOP' });
                    }
                    break;
                case 'RECONNECT':
                    this.setState({ view: 'NONE', expandDesktop: false });
                    break;
            }
            this.lastDesktopStatus = status;
        };
        this.initDesktopBase = () => {
            optIntoDesktopMode();
            registerStatusCallback(this.desktopStatusChange);
            let testWorked = false;
            const testSub = this.subManager.setSubscription(from(testConnection()).subscribe({
                next: () => {
                    testWorked = true;
                    this.initDesktopSdk();
                    this.prepareDesktopInstaller(true);
                },
                error: () => {
                    testWorked = true;
                    this.initDesktopSdk();
                    this.prepareDesktopInstaller();
                },
            }));
            if (isSafari()) {
                this.subManager.setSubscription(observableTimeout(2000).subscribe({
                    next: () => {
                        if (!testWorked) {
                            this.subManager.unsubscribe(testSub);
                            testWorked = true;
                            this.initDesktopSdk();
                            this.prepareDesktopInstaller();
                        }
                    },
                    error: error => {
                        new AsperaError('AsperaConnectInstaller: Desktop SDK could not timeout Safari', error);
                    },
                }));
            }
        };
        this.initConnectBase = () => {
            var _a, _b;
            if ((_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.asperaWeb) {
                (_b = window.asperaTransfer.asperaInstaller) === null || _b === void 0 ? void 0 : _b.installationJSON(this.handleJson);
                window.asperaTransfer.asperaWeb.addEventListener(Connect.EVENT.STATUS, this.connectStatusEventListener);
                window.asperaTransfer.asperaWeb.initSession(this.appId);
            }
            else {
                new AsperaError('AsperaConnectInstaller: Connect SDK was not available');
                notification.create('error', language.get('transferSystem.unableToStartConnect'));
            }
        };
    }
    get needsTest() {
        return /safari/i.test(navigator.userAgent) && !/chrome|crios|crmo/i.test(navigator.userAgent) && !/edge/i.test(navigator.userAgent);
    }
    get appId() {
        const { transferSystemData } = this.props;
        return `aspera_${transferSystemData.uniqueHash || clientEncrypt(transferSystemData.userName || 'user', Number(transferSystemData.userId))}`;
    }
    get currentSteps() {
        var _a, _b;
        const { view } = this.state;
        const steps = [];
        const useStoreLink = !!((_b = (_a = window.asperaTransfer) === null || _a === void 0 ? void 0 : _a.asperaInstaller) === null || _b === void 0 ? void 0 : _b.getExtensionStoreLink());
        let currentStep = 0;
        if (useStoreLink) {
            if (view !== 'PLUGIN')
                currentStep++;
            steps.push(React.createElement(ProgressStep, { complete: view !== 'PLUGIN', key: "0", title: language.get('transferSystem.installExtensionButton'), "aria-describedby": "install-extension-helper", onClick: this.installExtension, label: (React.createElement("div", { className: "progress-step-action" },
                    React.createElement("div", { className: "progress-step-action--text" },
                        React.createElement("p", { className: "cds--progress-label" }, language.get('transferSystem.installExtension')),
                        React.createElement("p", { className: "cds--progress-optional", id: "install-extension-helper" }, language.get('transferSystem.installExtensionHelp'))),
                    React.createElement("div", { className: "progress-step-action--button" },
                        React.createElement("div", { className: `cds--btn cds--btn--field cds--btn--${currentStep === 0 ? 'primary' : 'tertiary'}` }, language.get('transferSystem.installExtensionButton'))))) }));
        }
        steps.push(React.createElement(ProgressStep, { key: "1", complete: view === 'ENABLE', title: language.get('transferSystem.installConnectButton'), "aria-describedby": "install-client-helper", onClick: this.installConnect, label: (React.createElement("div", { className: "progress-step-action" },
                React.createElement("div", { className: "progress-step-action--text" },
                    React.createElement("p", { className: "cds--progress-label" }, language.get('transferSystem.installConnect')),
                    React.createElement("p", { className: "cds--progress-optional", id: "install-client-helper" }, language.get('transferSystem.installConnectHelp'))),
                React.createElement("div", { className: "progress-step-action--button" },
                    React.createElement("div", { className: `cds--btn cds--btn--field cds--btn--${currentStep === (useStoreLink ? 1 : 0) ? 'primary' : 'tertiary'}` }, language.get('transferSystem.installConnectButton'))))) }));
        if (this.needsTest) {
            if (view === 'ENABLE')
                currentStep++;
            steps.push(React.createElement(ProgressStep, { key: "2", title: language.get('transferSystem.refreshPageButton'), "aria-describedby": "refresh-page-helper", onClick: this.refreshPage, label: (React.createElement("div", { className: "progress-step-action" },
                    React.createElement("div", { className: "progress-step-action--text" },
                        React.createElement("p", { className: "cds--progress-label" }, language.get('transferSystem.refreshPage')),
                        React.createElement("p", { className: "cds--progress-optional", id: "refresh-page-helper" }, language.get('transferSystem.refreshPageHelp'))),
                    React.createElement("div", { className: "progress-step-action--button" },
                        React.createElement("div", { className: `cds--btn cds--btn--field cds--btn--${currentStep === 1 ? 'primary' : 'tertiary'}` }, language.get('transferSystem.refreshPageButton'))))) }));
        }
        return (React.createElement("div", { className: "installer-main-content--actions" },
            React.createElement("ul", { className: "cds--progress cds--progress--vertical" }, steps)));
    }
    get installScreen() {
        const { view } = this.state;
        const { transferSystemData } = this.props;
        const updateView = view === 'UPDATE';
        const hasGatewayBackup = !!transferSystemData.httpGatewayUrl;
        return (React.createElement("div", { className: "aspera-connect-installer--install", role: "dialog", "aria-modal": "true" },
            React.createElement("div", { className: "installer-main-content" },
                React.createElement("div", { className: "installer-main-content--wrapper" },
                    React.createElement("div", { className: "installer-main-content--heading" }, language.get(`transferSystem.${updateView ? 'updateHeader' : 'installHeader'}`)),
                    React.createElement("div", { className: "installer-main-content--info" }, language.getStyled(`transferSystem.${updateView ? 'updateText' : 'installText'}`, undefined, ['https://www.ibm.com/aspera/connect/'])),
                    this.currentSteps,
                    React.createElement("div", { className: "installer-main-content--skip", id: "skip-warning" }, language.get(`transferSystem.${hasGatewayBackup ? 'ifYouSkipWithHttp' : 'ifYouSkip'}`)),
                    React.createElement("button", { type: "button", "aria-describedby": "skip-warning", className: "installer-main-content--skip-action cds--btn cds--btn--ghost", onClick: this.handleSkip }, language.get('transferSystem.skipNow')))),
            React.createElement("div", { className: "installer-image" },
                React.createElement(ImageConnectFlow, null))));
    }
    get installDesktopScreen() {
        const { expandDesktop, focusDesktopInstall, desktopDragPosition, isDesktopSupportOpened } = this.state;
        const dragProps = {
            onDrag: this.handleDesktopDrag,
            onDragStart: this.handleDragStart,
            onDragEnd: undefined,
        };
        if (isFirefox()) {
            dragProps.onDrag = undefined;
            dragProps.onDragStart = undefined;
            dragProps.onDragEnd = this.handleDragEnd;
        }
        return (React.createElement("div", { className: "aspera-desktop-installer", style: { right: desktopDragPosition, width: this.desktopInstallerWidth } },
            React.createElement("div", Object.assign({ className: "aspera-desktop-installer--collapsed", onClick: () => this.setState({ expandDesktop: true }), draggable: true }, dragProps),
                React.createElement("div", { className: "aspera-desktop-installer--drag" }, getIcon('Draggable')),
                getIcon('ApplicationWeb'),
                React.createElement("span", { className: "aspera-desktop-installer--collapsed-title" }, language.get('transferSystem.asperaDesktop')),
                React.createElement(Button, { kind: "ghost", hasIconOnly: true, size: "sm", renderIcon: getIconComponent('Launch'), iconDescription: language.get('transferSystem.launchDesktop'), tooltipAlignment: "end", tooltipPosition: "top", onClick: (event) => this.launchDesktop(event) }),
                React.createElement(Button, { kind: "ghost", hasIconOnly: true, size: "sm", renderIcon: getIconComponent('Close'), iconDescription: language.get('transferSystem.desktopSkip'), tooltipAlignment: "end", tooltipPosition: "top", onClick: (event) => this.handleSkip(event) })),
            React.createElement(Theme, { theme: "g100" },
                React.createElement("div", { className: `aspera-desktop-installer--open ${expandDesktop ? 'visible' : ''}` },
                    React.createElement("div", { className: "aspera-desktop-installer--open-close-wrapper" },
                        React.createElement(Button, { kind: "ghost", hasIconOnly: true, renderIcon: getIconComponent('Minimize'), iconDescription: language.get('transferSystem.minimize'), tooltipAlignment: "end", tooltipPosition: "top", onClick: () => this.setState({ expandDesktop: false }) }),
                        React.createElement(Button, { kind: "ghost", hasIconOnly: true, renderIcon: getIconComponent('Close'), iconDescription: language.get('transferSystem.desktopSkip'), tooltipAlignment: "end", tooltipPosition: "top", onClick: this.handleSkip })),
                    React.createElement("div", { className: "aspera-desktop-installer--open-image", style: { backgroundImage: `url(${desktopAppExample})` } }),
                    React.createElement("div", { className: "aspera-desktop-installer--open-title" },
                        getIcon('ApplicationWeb'),
                        React.createElement("span", null, language.get('transferSystem.asperaDesktop'))),
                    React.createElement("div", { className: "aspera-desktop-installer--open-message" }, language.get('transferSystem.asperaDesktopAbout')),
                    this.linkForDesktopDownload.length > 1 ? (React.createElement(AsperaOverflowButton, { name: language.get('transferSystem.installDesktopButton'), kind: focusDesktopInstall ? 'primary' : 'tertiary', className: "aspera-desktop-installer--open-install", id: "desktop-install-overflow", items: this.linkForDesktopDownload.map((item, index) => {
                            return {
                                name: this.getPlatformString(item),
                                id: `desktop-install-overflow-${index}`,
                                onClick: () => this.installDesktop(item),
                            };
                        }) })) : (React.createElement(Button, { kind: focusDesktopInstall ? 'primary' : 'tertiary', size: "sm", className: "aspera-desktop-installer--open-install", disabled: !this.linkForDesktopDownload.length, onClick: () => this.installDesktop(this.linkForDesktopDownload[0]) }, language.get('transferSystem.installDesktopButton'))),
                    React.createElement("div", { className: 'aspera-desktop-installer--support' },
                        isDesktopSupportOpened ? React.createElement("div", null, language.get('transferSystem.havingTrouble')) : React.createElement(Link, { onClick: () => this.setState({ isDesktopSupportOpened: !isDesktopSupportOpened }) },
                            " ",
                            language.get('transferSystem.havingTrouble')),
                        isDesktopSupportOpened &&
                            React.createElement(UnorderedList, { className: 'aspera-desktop-support-options' },
                                React.createElement(ListItem, null,
                                    React.createElement(Link, { onClick: changeTransferClient, className: 'aspera-desktop-support-links' },
                                        " ",
                                        language.get('transferActivity.changeTransferClient'))),
                                React.createElement(ListItem, null,
                                    React.createElement(Link, { onClick: this.toggleDesktopMultiUserMode, className: 'aspera-desktop-support-links' },
                                        " ",
                                        getStorageItem(storageKeys.enableDesktopMultiUser) ? language.get('transferSystem.enableMultiUser') : language.get('transferSystem.disableMultiUser')),
                                    React.createElement(AsperaTooltip, { align: 'top' }, language.get('transferSystem.multiUserToolTip'))))),
                    React.createElement("div", { className: "aspera-desktop-installer--open-launch-text" }, language.get('transferSystem.alreadyHaveDesktop')),
                    React.createElement(Button, { kind: focusDesktopInstall ? 'tertiary' : 'primary', size: "md", renderIcon: getIconComponent('Launch'), className: "aspera-desktop-installer--open-launch", onClick: this.launchDesktop }, language.get('transferSystem.launchDesktop'))))));
    }
    get welcomeScreen() {
        const { productName } = this.props;
        return (React.createElement("div", { className: "aspera-connect-installer--welcome-modal" },
            React.createElement(Modal, { open: true, size: "sm", onRequestClose: this.goToInstall, onSecondarySubmit: this.handleSkip, onRequestSubmit: this.goToInstall, secondaryButtonText: language.get('transferSystem.welcomeSkip'), primaryButtonText: language.get('transferSystem.welcomeContinue'), preventCloseOnClickOutside: true },
                React.createElement(Theme, { theme: "g100" },
                    React.createElement("div", { className: "image-area" },
                        React.createElement(ImageConnectFlow, null)),
                    React.createElement("div", { className: "text-area" },
                        React.createElement("div", { className: "text-area--heading" }, language.get('transferSystem.welcomeTo', [productName])),
                        React.createElement("div", { className: "text-area--info" }, getEnvironmentVar('support_desktop_sdk') ? language.get('transferSystem.welcomeTextToUseDesktop') : language.get('transferSystem.welcomeText')))))));
    }
    get desktopWelcomeScreen() {
        const { transferSystemData } = this.props;
        const useConnectFinal = () => {
            this.setState({ view: 'NONE' });
            tracking.updateUserPreferences({ optIntoDesktop: false });
            tracking.track('Started Process', {
                process: 'Clicked Use Connect For Desktop Flow',
                successFlag: true,
                processType: 'connect',
            });
            setStorageItem(storageKeys.transferSystemChoice, 'CONNECT');
            this.initConnectBase();
        };
        const enableDesktop = () => {
            this.setState({ view: 'NONE', expandDesktop: true, focusDesktopInstall: true });
            tracking.updateUserPreferences({ optIntoDesktop: true });
            setStorageItem(storageKeys.transferSystemChoice, 'DESKTOP');
            tracking.track('Started Process', {
                process: 'Clicked Use Desktop For Desktop Flow',
                successFlag: true,
                processType: 'connect',
            });
            this.initDesktopBase();
        };
        const useConnect = () => {
            tracking.track('Started Process', {
                process: 'Clicked Use Connect For Desktop Flow Before Intercept',
                successFlag: true,
                processType: 'connect',
            });
            const modalInfo = modal.prepare();
            const modalProps = {
                modalHeading: language.get('transferSystem.areYouSure'),
                primaryButtonText: language.get('transferSystem.installNow'),
                secondaryButtonText: language.get('transferSystem.skip'),
                size: 'sm',
            };
            this.subManager.setSubscription(modal.create(modalInfo.id, modalProps, React.createElement(ModalBodyConfirm, { description: language.get('transferSystem.desktopAreYouSure'), modalClose: modalInfo.callback })).subscribe({
                next: () => {
                    enableDesktop();
                },
                error: () => {
                    useConnectFinal();
                },
            }));
        };
        return (React.createElement("div", { className: "aspera-connect-installer--welcome-modal desktop" },
            React.createElement(Modal, { open: true, onRequestClose: useConnect, onSecondarySubmit: transferSystemData.desktopDefaulted ? undefined : useConnect, onRequestSubmit: enableDesktop, secondaryButtonText: transferSystemData.desktopDefaulted ? undefined : language.get('transferSystem.useIbmAsperaConnect'), primaryButtonText: language.get(`transferSystem.${transferSystemData.desktopDefaulted ? 'installNow' : 'useIbmAsperaDesktop'}`), preventCloseOnClickOutside: true },
                React.createElement(Theme, { theme: "g100" },
                    React.createElement("div", { className: "image-area-desktop", style: { backgroundImage: `url(${desktopAppExample})` } }),
                    React.createElement("div", { className: "text-area" },
                        React.createElement("div", { className: "text-area--heading" }, language.get('transferSystem.asperaDesktopModalTitle')),
                        React.createElement("div", { className: "text-area--info" }, language.get(`transferSystem.${transferSystemData.desktopDefaulted ? 'asperaDesktopModalInfoNew' : 'asperaDesktopModalInfo'}`)))))));
    }
    get mainView() {
        const { view } = this.state;
        if (view === 'NONE') {
            return null;
        }
        else if (view === 'WELCOME') {
            return this.welcomeScreen;
        }
        else if (view === 'DESKTOP_WELCOME') {
            return this.desktopWelcomeScreen;
        }
        else if (view === 'DESKTOP') {
            return this.installDesktopScreen;
        }
        else {
            return this.installScreen;
        }
    }
    /** IMPORTANT: put subscriptions first before conditional statements */
    componentDidMount() {
        const { transferSystemData } = this.props;
        const transferSystem = getStorageItem(storageKeys.transferSystemChoice) || undefined;
        this.subManager.setSubscription(tracking.desktopOpenRequest.subscribe({
            next: () => {
                this.setState({ expandDesktop: true, view: 'DESKTOP' });
            },
            error: error => {
                new AsperaError('AsperaConnectInstaller: unable to watch for desktop open requests', error);
            },
        }));
        this.subManager.setSubscription(fromEvent(window, 'resize').subscribe({
            next: () => {
                this.dragPositionSubject.next(-1);
            },
            error: error => {
                new AsperaError('AsperaConnectInstaller: Unable to watch resize event', error);
            },
        }));
        this.subManager.setSubscription(this.dragPositionSubject.asObservable().pipe(throttleTime(50)).subscribe({
            next: (position) => {
                const windowMax = window.innerWidth;
                if (position < 0) {
                    this.setState({ desktopDragPosition: (windowMax / 2) - (this.desktopInstallerWidth / 2) });
                    return;
                }
                const dragBarPosition = windowMax - position + this.desktopInstallerWidth / 2;
                if (dragBarPosition < this.desktopInstallerWidth + 16) {
                    this.setState({ desktopDragPosition: 16 });
                    return;
                }
                else if (dragBarPosition > windowMax - this.desktopInstallerWidth / 2) {
                    this.setState({ desktopDragPosition: windowMax - this.desktopInstallerWidth - 16 });
                    return;
                }
                this.setState({ desktopDragPosition: windowMax - (position + this.desktopInstallerWidth / 2) });
            },
            error: error => {
                new AsperaError('AsperaConnectInstaller: Unable to watch drag position subject', error);
            },
        }));
        this.dragPositionSubject.next(-1);
        if (transferSystemData.disableConnect ||
            runningWithAsperaMobile() ||
            isMobile()) {
            setupTransferMonitor();
            this.setState({ view: 'NONE' });
        }
        else {
            if (getEnvironmentVar('support_desktop_sdk')) {
                const optIntoDesktop = tracking.getUserPreference('optIntoDesktop');
                if (typeof optIntoDesktop === 'boolean' || typeof transferSystem === 'string') {
                    if (optIntoDesktop || transferSystem === 'DESKTOP') {
                        this.initDesktopBase();
                        return;
                    }
                }
                else {
                    this.setState({ view: 'DESKTOP_WELCOME' });
                    return;
                }
            }
            this.initConnectBase();
            if (getStorageItem(storageKeys.skipTransferSystem)) {
                setupTransferMonitor(true);
                this.setState({ view: 'NONE' });
            }
        }
    }
    componentDidUpdate() {
        const { hasFocused } = this.state;
        if (!hasFocused) {
            const asperaConnectLink = document.querySelector('.installer-main-content--info .cds--link');
            if (asperaConnectLink) {
                this.setState({ hasFocused: true }, () => asperaConnectLink.focus());
            }
        }
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
        if (this.timer)
            clearTimeout(this.timer);
    }
    render() {
        const { className } = this.props;
        return (React.createElement("div", { className: `aspera-connect-installer ${className ? className : ''}`, "data-auto": "connect-installer" }, this.mainView));
    }
}
